import { parseBooleanVal } from "Utils/utils";
import { getAdvanceDisplayStatusLabel } from "pages/Rostering/Calendar/advanceShiftCalendarUtils";

export const workerFullName = (worker) =>
  `${worker?.first_name || ""} ${worker?.last_name || ""}`.trim();

const isInactiveWorker = (worker) => {
  if (worker == null || worker.status == null) return false;
  return (
    worker.status === false ||
    worker.status === 0 ||
    String(worker.status).toLowerCase() === "inactive"
  );
};

const hasOnlyInactiveParticipants = (participants = []) => {
  if (!Array.isArray(participants) || participants.length === 0) return false;
  const withStatus = participants.filter(
    (participant) => participant?.status != null && participant.status !== ""
  );
  if (withStatus.length === 0) return false;
  return withStatus.every(
    (participant) => String(participant.status).toLowerCase() === "inactive"
  );
};

export const parseDashboardDateTime = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const raw = String(value).trim();
  if (!raw) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const parsed = new Date(`${raw}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(raw)) {
    const parsed = new Date(raw.replace(" ", "T"));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const startOfLocalDay = (date) => {
  const day = new Date(date);
  day.setHours(0, 0, 0, 0);
  return day;
};

/**
 * Previous calendar days are past. Same-day shifts that already ended are past.
 * Overnight in-progress is kept only while end_time is still in the future.
 */
export const isPastDashboardShift = (shift, now = new Date()) => {
  const status = String(shift?.status || "").toLowerCase();
  if (status === "expired") return true;

  const end = parseDashboardDateTime(shift?.end_time);
  if (end && end < now) return true;

  const start = parseDashboardDateTime(shift?.start_time);
  const shiftDay = parseDashboardDateTime(shift?.shift_date) || (start ? startOfLocalDay(start) : null);
  if (shiftDay && startOfLocalDay(shiftDay) < startOfLocalDay(now)) {
    if (end && end >= now) return false;
    return true;
  }

  return false;
};

export const isDashboardShiftVisible = (shift, now = new Date()) => {
  if (isPastDashboardShift(shift, now)) return false;
  if (hasOnlyInactiveParticipants(shift?.participants)) return false;
  if (!parseBooleanVal(shift?.is_multi_assignment) && isInactiveWorker(shift?.worker)) {
    return false;
  }
  return true;
};

/**
 * Expand advance shifts into one card per assignment.
 * Basic shifts stay as a single card.
 */
export const expandShiftsForDashboard = (shifts = [], now = new Date()) => {
  const rows = [];

  shifts.forEach((shift) => {
    if (!isDashboardShiftVisible(shift, now)) {
      return;
    }

    const isAdvance = parseBooleanVal(shift?.is_multi_assignment);

    if (!isAdvance) {
      rows.push({
        ...shift,
        _rowKey: `shift-${shift?.id}`,
        _isAdvance: false,
      });
      return;
    }

    const assignments = (Array.isArray(shift?.workers) ? shift.workers : []).filter(
      (entry) => !isInactiveWorker(entry?.worker)
    );

    // Filled-only advance whose workers are all inactive should not render.
    if (
      assignments.length === 0 &&
      Array.isArray(shift?.workers) &&
      shift.workers.length > 0 &&
      shift.workers.every((entry) => isInactiveWorker(entry?.worker))
    ) {
      return;
    }

    // Whole-open advance with no assignment rows
    if (assignments.length === 0) {
      rows.push({
        ...shift,
        worker: null,
        worker_id: null,
        _rowKey: `shift-${shift?.id}-open`,
        _isAdvance: true,
        _isOpenAssignment: true,
      });
      return;
    }

    // One individual card per assignment.
    // Keep shift.start_time/end_time for display — assignment datetimes are
    // cast to UTC ISO and formatDateTimeWithAMPM would show the wrong local
    // time (e.g. 5:00 AM in Asia/Karachi).
    assignments.forEach((entry, index) => {
      const isOpen = parseBooleanVal(entry?.is_open) || !entry?.worker;
      const worker = entry?.worker || null;

      rows.push({
        ...shift,
        worker,
        worker_id: worker?.id ?? null,
        // Per-assignment clock/progress so partial in-progress workers show
        // the dashboard in-progress indicator (container worker_start_time is null on V5).
        progress: entry?.progress ?? null,
        worker_start_time: entry?.worker_start_time ?? null,
        worker_end_time: entry?.worker_end_time ?? null,
        _rowKey: `shift-${shift?.id}-a-${
          entry?.assignment_id ?? worker?.id ?? index
        }`,
        _isAdvance: true,
        _isOpenAssignment: isOpen,
      });
    });
  });

  return sortDashboardShiftRows(rows);
};

const isOpenDashboardRow = (row) =>
  Boolean(row?._isOpenAssignment) ||
  parseBooleanVal(row?.is_open_shift) ||
  (!row?._isAdvance && row?.worker_id == null);

const dashboardRowStartMs = (row) => {
  const parsed = parseDashboardDateTime(row?.start_time);
  return parsed ? parsed.getTime() : Number.MAX_SAFE_INTEGER;
};

const sortDashboardShiftRows = (rows) =>
  [...rows].sort((a, b) => {
    const openFirst =
      Number(isOpenDashboardRow(b)) - Number(isOpenDashboardRow(a));
    if (openFirst !== 0) return openFirst;
    return dashboardRowStartMs(a) - dashboardRowStartMs(b);
  });

export const getScheduledShiftDisplay = (element) => {
  if (element?._isAdvance) {
    const isOpen = Boolean(element?._isOpenAssignment);
    return {
      title: isOpen ? "open Shift" : workerFullName(element?.worker),
      isAdvance: true,
      useOpenAvatar: isOpen,
      progressLabel: getAdvanceDisplayStatusLabel(element),
    };
  }

  // Basic — original individual card behavior
  const isOpen = element?.worker_id == null;
  return {
    title: isOpen ? "open Shift" : workerFullName(element?.worker),
    isAdvance: false,
    useOpenAvatar: isOpen,
    progressLabel: null,
  };
};
